import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, KanbanSquare, Table2, Download, Upload, Building2, Sparkles } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, Money, EmptyState, STAGE_COLORS } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const emptyOpp = { title: "", org_name: "", stage: "Lead", source: "Direct", estimated_value: 0, currency: "USD", service_interests: [], next_action: "", next_action_date: "", description: "" };

export default function Pipeline() {
  const qc = useQueryClient();
  const [view, setView] = useState("kanban");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOpp);

  const { data: opps, isLoading } = useQuery({ queryKey: ["opps"], queryFn: async () => (await api.get("/crm/opportunities")).data });
  const { data: meta } = useQuery({ queryKey: ["crm-meta"], queryFn: async () => (await api.get("/crm/meta")).data });

  const save = useMutation({
    mutationFn: (payload) => editing?.id ? api.put(`/crm/opportunities/${editing.id}`, payload) : api.post("/crm/opportunities", payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opps"] }); setEditing(null); toast.success("Opportunity saved"); },
  });
  const moveStage = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/crm/opportunities/${id}/stage`, { stage }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opps"] }),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/crm/opportunities/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opps"] }); setEditing(null); toast.success("Deleted"); },
  });

  const openNew = () => { setForm(emptyOpp); setEditing({}); };
  const openEdit = (o) => { setForm({ ...emptyOpp, ...o, next_action_date: o.next_action_date || "" }); setEditing(o); };
  const submit = () => save.mutate({ ...form, estimated_value: Number(form.estimated_value) || 0, next_action_date: form.next_action_date || null });

  const exportCsv = () => {
    const rows = opps || [];
    const cols = ["title", "org_name", "stage", "source", "estimated_value", "currency", "next_action", "next_action_date"];
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => `"${(r[c] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "pipeline.csv"; a.click();
  };

  if (isLoading || !meta) return <Loading />;
  const stages = meta.stages;
  const toggleInterest = (s) => setForm((f) => ({ ...f, service_interests: f.service_interests.includes(s) ? f.service_interests.filter((x) => x !== s) : [...f.service_interests, s] }));

  return (
    <div>
      <PageHeader eyebrow="CRM & Pipeline" title="Sales Pipeline"
        description="Track prospects from lead to won across your seven stages. Kanban and table views, source tracking, and duplicate hints."
        actions={
          <>
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button onClick={() => setView("kanban")} data-testid="view-kanban" className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === "kanban" ? "bg-emerald-600 text-white" : "text-slate-600"}`}><KanbanSquare className="w-4 h-4" /> Board</button>
              <button onClick={() => setView("table")} data-testid="view-table" className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === "table" ? "bg-emerald-600 text-white" : "text-slate-600"}`}><Table2 className="w-4 h-4" /> Table</button>
            </div>
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
            <Button onClick={openNew} data-testid="new-opportunity" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New</Button>
          </>
        } />

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-6">
          {stages.map((stage) => {
            const items = (opps || []).filter((o) => o.stage === stage);
            const val = items.reduce((s, o) => s + (o.estimated_value || 0), 0);
            return (
              <div key={stage} className="flex-shrink-0 w-72" data-testid={`stage-col-${stage.toLowerCase()}`}>
                <div className="flex items-center justify-between px-1 mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-md border ${STAGE_COLORS[stage]}`}>{stage}</span>
                    <span className="text-xs text-slate-400">{items.length}</span>
                  </div>
                  <span className="text-[11px] text-slate-400 font-mono"><Money value={val} /></span>
                </div>
                <div className="space-y-2.5 min-h-[100px]">
                  {items.map((o) => (
                    <motion.div key={o.id} layout onClick={() => openEdit(o)} data-testid={`opp-card-${o.id}`}
                      className="bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-200 p-3.5 cursor-pointer transition-all">
                      <div className="font-medium text-sm text-slate-900 leading-snug">{o.title}</div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1"><Building2 className="w-3 h-3" /> {o.org_name || "—"}</div>
                      <div className="flex items-center justify-between mt-2.5">
                        <span className="text-sm font-semibold text-emerald-700"><Money value={o.estimated_value} currency={o.currency} /></span>
                        <span className="text-[10px] text-slate-400 font-mono">{o.source}</span>
                      </div>
                      {o.diagnostic_id && <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded"><Sparkles className="w-2.5 h-2.5" /> from diagnostic</div>}
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Opportunity</TableHead><TableHead>Organisation</TableHead><TableHead>Stage</TableHead>
                <TableHead>Value</TableHead><TableHead>Source</TableHead><TableHead>Next action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(opps || []).map((o) => (
                <TableRow key={o.id} onClick={() => openEdit(o)} data-testid={`opp-row-${o.id}`} className="cursor-pointer">
                  <TableCell className="font-medium text-slate-900">{o.title}</TableCell>
                  <TableCell className="text-slate-600">{o.org_name}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-md border ${STAGE_COLORS[o.stage]}`}>{o.stage}</span></TableCell>
                  <TableCell className="font-semibold text-slate-800"><Money value={o.estimated_value} currency={o.currency} /></TableCell>
                  <TableCell className="text-slate-500 text-sm">{o.source}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{o.next_action || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(opps || []).length === 0 && <div className="p-8"><EmptyState icon={KanbanSquare} title="No opportunities yet" description="Add your first opportunity to build your pipeline." action={<Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> New opportunity</Button>} /></div>}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit opportunity" : "New opportunity"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="opp-title" className="mt-1.5" placeholder="Acme Corp — ESG Reporting" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Organisation</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="opp-org" className="mt-1.5" /></div>
              <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="opp-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Estimated value</Label><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} data-testid="opp-value" className="mt-1.5" /></div>
            </div>
            <div><Label>Service interests</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {meta.service_interests.map((s) => (
                  <button key={s} type="button" onClick={() => toggleInterest(s)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.service_interests.includes(s) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Next action</Label><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Next action date</Label><Input type="date" value={form.next_action_date || ""} onChange={(e) => setForm({ ...form, next_action_date: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter className="gap-2">
            {editing?.id && <Button variant="ghost" className="text-rose-600 mr-auto" onClick={() => del.mutate(editing.id)} data-testid="opp-delete">Delete</Button>}
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={!form.title} data-testid="opp-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
